// src/pages/Admin/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../api';
import { Users, Bike, ShoppingBag, CreditCard, Headphones, TrendingUp, Calendar } from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await adminAPI.getPlatformStats();
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-12">Loading dashboard...</div>;

  const summary = stats?.summary || {};
  const recentOrders = stats?.recentActivities?.orders || [];

  const statsCards = [
    { icon: <Users className="h-6 w-6" />, label: 'Total Users', value: summary.totalUsers || 0, color: 'from-blue-500 to-cyan-500', subvalue: `${summary.totalCustomers || 0} customers` },
    { icon: <Bike className="h-6 w-6" />, label: 'Riders', value: summary.totalRiders || 0, color: 'from-green-500 to-emerald-500', subvalue: `${summary.activeListings || 0} active listings` },
    { icon: <ShoppingBag className="h-6 w-6" />, label: 'Total Orders', value: summary.totalOrders || 0, color: 'from-purple-500 to-pink-500', subvalue: `${summary.completedOrders || 0} completed` },
    { icon: <CreditCard className="h-6 w-6" />, label: 'Platform Earnings', value: `₦${(summary.totalEarnings || 0).toLocaleString()}`, color: 'from-yellow-500 to-amber-500', subvalue: `${summary.totalTransactions || 0} transactions` },
    { icon: <Headphones className="h-6 w-6" />, label: 'Open Tickets', value: summary.openSupportTickets || 0, color: 'from-red-500 to-orange-500', subvalue: 'awaiting response' },
    { icon: <TrendingUp className="h-6 w-6" />, label: 'Platform Fee', value: '10%', color: 'from-indigo-500 to-purple-500', subvalue: 'per completed order' },
  ];

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome back, Admin. Here's what's happening on CarryNGo.</p>
      </motion.div>

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8"
      >
        {statsCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.02 }}
            className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 lg:p-5 border border-white/20 shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`h-10 w-10 lg:h-12 lg:w-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                {React.cloneElement(stat.icon, { className: "h-5 w-5 lg:h-6 lg:w-6 text-white" })}
              </div>
              <TrendingUp className="h-4 w-4 lg:h-5 lg:w-5 text-emerald-500" />
            </div>
            <div className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
            <div className="text-xs lg:text-sm text-gray-600">{stat.label}</div>
            <p className="text-xs text-gray-500 mt-1">{stat.subvalue}</p>
          </motion.div>
        ))}
      </motion.div>

      {/* Recent Orders */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 shadow-lg"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-600" />
            Recent Orders
          </h2>
        </div>
        {recentOrders.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No recent orders</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Order ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Rider</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {recentOrders.map((order: any) => (
                  <tr key={order.id || order._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{order.orderId}</td>
                    <td className="px-4 py-3">{order.customer?.fullName || 'N/A'}</td>
                    <td className="px-4 py-3">{order.rider?.fullName || 'N/A'}</td>
                    <td className="px-4 py-3">₦{order.price?.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{new Date(order.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Helper function (keep inside same file)
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-blue-100 text-blue-800',
    in_progress: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export default AdminDashboard;